/**
 * Locale-driven formatting.
 *
 * `MoneyAmount` is the only place currency is decided: integer minor units plus an ISO
 * 4217 code in, a correctly grouped local string out. An amount never travels without its
 * currency. See docs/03-design-system.md §13.1.
 */

const MINOR_UNITS: Record<string, number> = { JPY: 0, KRW: 0, VND: 0, IDR: 0, CLP: 0 };

/**
 * Intl formatters, built once and kept.
 *
 * Every Intl constructor does an ICU locale lookup, and these are called from the render
 * path: RelativeTime re-renders on every clock tick, once per visible conversation row.
 * Fifty rows was fifty RelativeTimeFormat constructions a second to produce text that
 * changes once a minute. The instances are immutable and safe to share.
 * docs/08-design-system-v2.md §3 P2.
 */
const numberFormats = new Map<string, Intl.NumberFormat>();
const relativeFormats = new Map<string, Intl.RelativeTimeFormat>();

function cached<T>(store: Map<string, T>, key: string, build: () => T): T {
  const hit = store.get(key);
  if (hit !== undefined) return hit;
  const made = build();
  store.set(key, made);
  return made;
}

export function minorUnitsFor(currency: string): number {
  return MINOR_UNITS[currency.toUpperCase()] ?? 2;
}

/** Integer minor units → a locale-correct string. ₹1,23,456 in en-IN, R$ 1.234,56 in pt-BR. */
export function formatMoney(minor: number, currency: string, locale: string): string {
  const digits = minorUnitsFor(currency);
  const format = cached(
    numberFormats,
    `${locale}|${currency}|${digits}`,
    () =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }),
  );
  return format.format(minor / 10 ** digits);
}

export function formatCount(n: number, locale: string): string {
  return cached(numberFormats, `${locale}|count`, () => new Intl.NumberFormat(locale)).format(n);
}

export function formatRelative(from: number, to: number, locale: string): string {
  const seconds = Math.round((from - to) / 1000);
  const rtf = cached(
    relativeFormats,
    locale,
    () => new Intl.RelativeTimeFormat(locale, { numeric: "auto", style: "narrow" }),
  );
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
    ["second", 1],
  ];
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size || unit === "second") {
      return rtf.format(Math.trunc(seconds / size), unit);
    }
  }
  return rtf.format(0, "second");
}

/** Scripts written right to left. Direction is a property of the SCRIPT, not the country —
 *  Arabic in Egypt and Arabic in the UAE are both RTL, and English in the UAE is not. */
const RTL_LANGUAGES = new Set(["ar", "he", "fa", "ur", "ps", "sd", "yi", "dv", "ckb"]);

export function directionFor(locale: string): "ltr" | "rtl" {
  const language = locale.split("-")[0]?.toLowerCase() ?? "";
  return RTL_LANGUAGES.has(language) ? "rtl" : "ltr";
}

/** Per-script line-height. Devanagari and Arabic need the room their ascenders take. */
export function lineHeightFor(script: "latin" | "devanagari" | "arabic" | "cjk"): number {
  return { latin: 1.45, devanagari: 1.53, arabic: 1.57, cjk: 1.5 }[script];
}
export * from "./countries";
